import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { notifyRolesChanged, useLiveRoles } from "@/lib/liveRoles";
import PaidLivePaywall from "@/components/live-room/PaidLivePaywall";
import { requestVipCheck } from "@/components/vip/VipAwardAnnouncer";
import { setBroadcasting } from "@/lib/liveAudioAlerts";
import { toast } from "sonner";
import GiftPicker from "@/components/GiftPicker";
import CoHostInviteModal from "@/components/CoHostInviteModal";
import BattleModal from "@/components/BattleModal";
import BattleBanner from "@/components/BattleBanner";
import LiveGiftOverlay from "@/components/LiveGiftOverlay";
import LiveToolsPanel from "@/components/live-room/LiveToolsPanel";
import GamesLaunchSheet from "@/components/live-room/GamesLaunchSheet";
import LiveGameCard from "@/components/live-room/LiveGameCard";
import DareOrShotSheet from "@/components/live-room/DareOrShotSheet";
import DareGameCard from "@/components/live-room/DareGameCard";
import LiveBlastSheet from "@/components/live-room/LiveBlastSheet";
import TriviaTopChip from "@/components/live-room/TriviaTopChip";
import LiveChatOverlay from "@/components/LiveChatOverlay";
import LiveGoalBar from "@/components/LiveGoalBar";
import LiveWheelModal from "@/components/LiveWheelModal";
import LiveRoomAfkBanner from "@/components/live-room/LiveRoomAfkBanner";
import LiveRoomHearts from "@/components/live-room/LiveRoomHearts";
import LiveRoomStage from "@/components/live-room/LiveRoomStage";
import LiveRoomTopBar from "@/components/live-room/LiveRoomTopBar";
import LiveRoomRequestCohostButton from "@/components/live-room/LiveRoomRequestCohostButton";
// TopGifterChip superseded by LiveStatusOverlay (always-visible Top 3 for all roles)
// eslint-disable-next-line no-unused-vars
import TopGifterChip from "@/components/live-room/TopGifterChip";
import LiveRoomActionRail from "@/components/live-room/LiveRoomActionRail";
import BattleGiftCta from "@/components/live-room/BattleGiftCta";
import LiveRoomViewersDrawer from "@/components/live-room/LiveRoomViewersDrawer";
import LiveRoomCohostBanner from "@/components/live-room/LiveRoomCohostBanner";
import LiveRoomCohostRequests from "@/components/live-room/LiveRoomCohostRequests";
import LiveRoomHostStageControls from "@/components/live-room/LiveRoomHostStageControls";
import LiveRoomCreatorTools from "@/components/live-room/LiveRoomCreatorTools";
import LiveRoomAdminDrawer from "@/components/live-room/LiveRoomAdminDrawer";
import BattlePeekTile from "@/components/live-room/BattlePeekTile";
import BattleSplitLayout from "@/components/live-room/BattleSplitLayout";
import useLiveRoomWS, { LiveRoomSocketContext } from "@/hooks/useLiveRoomWS";
import useLiveMountTelemetry from "@/hooks/useLiveMountTelemetry";
import { UserBadgeProvider } from "@/components/UserBadge";
import { useAuth } from "@/context/AuthContext";
import ReportModal from "@/components/ReportModal";
import { useControlCenter } from "@/context/ControlCenterContext";
import { useMuxEnvKey } from "@/lib/mux";
import {
  useLiveKitToken,
  useCohostStatusWatch,
  useHostAfkFromSocket,
  useViewersList,
  useFreshStream,
  useNextLive,
  useBattlePoll,
  useMuxLiveTracking,
} from "@/components/live-room/useLiveRoomHooks";

export default function LiveRoom({ stream, onClose, onSurf, mode = "vault", tokenEndpoint, community, onKickParticipant }) {
  const { user } = useAuth();
  const cc = useControlCenter();
  useEffect(() => { cc.setLiveRoomOpen?.(true); return () => cc.setLiveRoomOpen?.(false); }, [cc.setLiveRoomOpen]); // eslint-disable-line react-hooks/exhaustive-deps
  const muxEnvKey = useMuxEnvKey();
  const isCommunity = mode === "community";
  // Duo layout — count of publisher tiles reported up from FullScreenStage.
  const [publisherCount, setPublisherCount] = useState(0);

  // Capability gates — Community/private Live suppresses stream-only features
  // like Wheel/Battle/Cohost/GoalBar/HostStageControls that assume a public
  // livestream doc backing them. Chat/Gifts/Reactions/Viewers/AV stay ON.
  // Live-parity (Oct 2026): Community Live is functionally identical to
  // regular Live — same stage, band geometry, tools, co-host lifecycle, chat.
  const caps = {
    hasWheel: true,
    hasBattle: !isCommunity, // 1v1 battles pair two public streams; private rooms aren't discoverable
    hasCohost: true,
    hasGoalBar: true,
    hasNextLive: !isCommunity, // surfing moves between public rooms only
    hasBrb: true,
    hasStreamPoll: true,
    canKickParticipant: isCommunity && !!onKickParticipant,
  };

  const { token, serverUrl, role, err, paywall, refresh: refreshToken } = useLiveKitToken(stream.id, { endpoint: tokenEndpoint });

  const [giftOpen, setGiftOpen] = useState(false);
  const [giftTarget, setGiftTarget] = useState(null); // {id, name} of a co-host on stage; null → host
  const [cohostOpen, setCohostOpen] = useState(false);
  const [battleOpen, setBattleOpen] = useState(false);
  const [wheelOpen, setWheelOpen] = useState(false);
  const [liveToolsOpen, setLiveToolsOpen] = useState(false);
  const [gamesOpen, setGamesOpen] = useState(false);
  const [gameType, setGameType] = useState("poll");
  const [activeGame, setActiveGame] = useState(null);
  const [dareOpen, setDareOpen] = useState(false);
  const [dare, setDare] = useState({ game: null, can_join: false });
  const refreshDare = useCallback(() => {
    api.get(`/live/${stream.id}/dare/active`).then(({ data }) => setDare(data)).catch(() => {});
  }, [stream.id]);
  useEffect(() => { refreshDare(); }, [refreshDare, role]);
  const [giftTotal, setGiftTotal] = useState(0);
  const [topGifters, setTopGifters] = useState([]);

  useEffect(() => {
    // Initial authoritative session gift count + top gifters (survives refresh/reconnect).
    api.get(`/live/${stream.id}/gift-count`).then(({ data }) => setGiftTotal(data.total)).catch(() => {});
    api.get(`/live/${stream.id}/top-gifters`).then(({ data }) => setTopGifters(data.top || [])).catch(() => {});
  }, [stream.id]);
  const [blastOpen, setBlastOpen] = useState(false);
  const [triviaTrigger, setTriviaTrigger] = useState(0);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const isAdminUser = !!user && (user.role === "admin" || user.is_owner);
  // Short landscape (phones rotated): there is no room for a chat band under
  // the video, so the stage takes the left 66% and chat owns the right column.
  const [vp, setVp] = useState(() => ({ w: window.innerWidth, h: window.innerHeight }));
  useEffect(() => {
    const on = () => setVp({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", on);
    window.addEventListener("orientationchange", on);
    return () => { window.removeEventListener("resize", on); window.removeEventListener("orientationchange", on); };
  }, []);
  const shortLandscape = vp.w > vp.h && vp.h < 520;

  const [pendingRequests, setPendingRequests] = useState(0);
  useEffect(() => {
    const on = (e) => setPendingRequests(Number(e.detail) || 0);
    window.addEventListener("pandax:cohost-requests-count", on);
    return () => window.removeEventListener("pandax:cohost-requests-count", on);
  }, []);
  const [copied, setCopied] = useState(false);
  const [showViewers, setShowViewers] = useState(false);
  const [hearts, setHearts] = useState([]);
  const [heartsTotal, setHeartsTotal] = useState(Number(stream?.hearts_total || 0));
  const pendingHearts = useRef(0);
  const heartFlush = useRef(null);
  const heartCounter = useRef(0);
  const stageRef = useRef(null);
  // Part 6 — measured header height drives the multi-box band offset so the
  // top row of faces starts below the chips (solo stays full-bleed).
  const headerRef = useRef(null);
  const rootRef = useRef(null);
  useEffect(() => {
    const el = headerRef.current; const root = rootRef.current;
    if (!el || !root) return undefined;
    const apply = () => root.style.setProperty("--live-header-h", `${Math.ceil(el.getBoundingClientRect().height)}px`);
    apply();
    const ro = new ResizeObserver(apply); ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const touchStartRef = useRef(null);
  const [afk, setAfk] = useState(null);

  const isHost = role === "host" || stream?.host_id === user?.id;
  // Pair 3 — host-selected room role of the signed-in member (Admin moderates like the host; Moderator = waiting guests only).
  const { roles: roomRoles } = useLiveRoles(stream?.id);
  const roomRole = isHost ? "host" : (roomRoles[user?.id] || "viewer");
  const canModerate = isHost || roomRole === "admin";
  const isCohost = role === "cohost";
  // Publishers = host + accepted co-hosts, in Community rooms too.
  const canPublish = isHost || isCohost;

  // Unclean exits (tab closed, app killed, navigated away): fire a beacon so
  // the grant is revoked immediately instead of waiting for the socket grace.
  useEffect(() => {
    if (!isCohost || isHost) return undefined;
    const bye = () => {
      try { navigator.sendBeacon?.(`${process.env.REACT_APP_BACKEND_URL}/api/live/${stream.id}/cohost/leave`, new Blob([], { type: "text/plain" })); } catch { /* best effort */ }
    };
    window.addEventListener("pagehide", bye);
    window.addEventListener("beforeunload", bye);
    return () => { window.removeEventListener("pagehide", bye); window.removeEventListener("beforeunload", bye); };
  }, [isCohost, isHost, stream.id]);


  // Publishers never get audible chat/gift alerts — the shared alert
  // AudioContext is torn down so nothing runs beside the live mic.
  useEffect(() => {
    setBroadcasting(canPublish);
    return () => setBroadcasting(false);
  }, [canPublish]);

  // Stage 4(b) — Layout / pin / spotlight state comes from the freshStream
  // doc (poll). Local overrides land here first for optimistic UX; the next
  // poll then reconciles with server truth. Fallback ensures the shape is
  // always defined so LiveRoomStage doesn't need null-guards.
  const DEFAULT_STAGE = { layout: "auto", pinned_id: null, spotlight_id: null };
  const [stageOverride, setStageOverride] = useState(null);

  // Stage 4(c) — unified room WebSocket. When connected, its `stage`/
  // `viewerCount` supersede the 6-second poll (< 100ms updates). Non-blocking:
  // if the WS never connects, the 6s poll keeps everything working.
  // Phase 4B follow-up — also handle `battle:score` / `battle:end` on this
  // long-lived room WS (already warm since room mount) so scoreboard ticks
  // land in < 200ms instead of waiting for the 4s useBattlePoll fallback.
  // Functional setBattle filter avoids any stale-closure race with the
  // useBattlePoll's initial fetch — we simply drop frames that don't match
  // the currently loaded battle id.
  // Iter68/F3 — audio-bridge state lives ON the battle object. The 4s
  // useBattlePoll snapshot is server-authoritative (persisted by the bridge
  // via _set_bridge_state), so late joiners and reconnecting viewers get the
  // correct state on mount; WS battle:audio_bridge:* frames patch the same
  // field for <200ms subsequent updates.
  const setBattleRef = useRef(null);
  const {
    stage: wsStage,
    viewerCount: wsViewerCount,
    ended: wsEnded,
    wsConnected,
    socket: roomSocket,
  } = useLiveRoomWS(stream.id, {
    onGift: (_gift, msg) => {
      // Server-authoritative session total + ranking — replay/reconnect safe
      // (absolute values, not increments), counts only backend-accepted gifts.
      if (msg?.session_gift_total != null) setGiftTotal(msg.session_gift_total);
      if (msg?.top_gifters) setTopGifters(msg.top_gifters);
    },
    onEvent: (msg) => {
      if (!msg) return;
      if (typeof msg.channel === "string" && msg.channel.startsWith("moderation:role_")) notifyRolesChanged();
      if (typeof msg.channel === "string" && msg.channel.startsWith("cohost:")) refreshDare();
      if (msg.channel === "entry:revoked" && msg.user_id === user?.id) { toast.error("Your entry to this Live was reversed."); refreshToken(); }
      if (msg.channel === "hearts:update" && msg.hearts_total != null) setHeartsTotal((t) => Math.max(t, Number(msg.hearts_total)));
      // Iter61 — real-time BRB banner clear / show without waiting for
      // the 5s useStreamStatePoll. Fixes the "15-minute banner won't clear
      // after host returns" bug.
      if (msg.channel === "game:opened" && msg.game) {
        setActiveGame({ ...msg.game, my_answer: null });
        return;
      }
      if (msg.channel === "dare:update" && msg.game) {
        setDare((d) => ({ ...d, game: msg.game, can_join: msg.game.players?.some((p) => p.user_id === user?.id) ? false : d.can_join }));
        if (msg.game.status === "open" && !msg.game.round) refreshDare();
        return;
      }
      if (msg.channel === "game:tally" && msg.game_id) {
        setActiveGame((g) => (g && g.id === msg.game_id ? { ...g, options: msg.options } : g));
        return;
      }
      if (msg.channel === "game:revealed" && msg.game) {
        setActiveGame((g) => ({ ...(g || {}), ...msg.game, my_answer: g?.my_answer ?? null }));
        if (msg.game.type === "trivia") setTriviaTrigger(Date.now());
        return;
      }
      if (msg.channel === "brb:changed") {
        if (msg.afk) setAfk(msg.afk);
        else if (msg.brb_active === false) setAfk((a) => a ? { ...a, brb_active: false } : a);
        return;
      }
      // Iter68/F3 — audio bridge lifecycle events patch the battle object
      // (same field the 4s poll snapshot delivers). Purely additive; failure
      // path is "do nothing" so the client keeps its legacy peek audio.
      if (typeof msg.channel === "string" && msg.channel.startsWith("battle:audio_bridge:")) {
        const setter = setBattleRef.current;
        if (setter) {
          setter((b) =>
            b && b.id === msg.battle_id ? { ...b, audio_bridge_state: msg.state || null } : b
          );
        }
        return;
      }
      if (msg.channel !== "battle:score" && msg.channel !== "battle:end") return;
      const setter = setBattleRef.current;
      if (!setter) return;
      setter((b) => {
        if (!b || b.id !== msg.battle_id) return b;
        if (msg.channel === "battle:score") {
          return { ...b, host_score: msg.host_score, opponent_score: msg.opponent_score };
        }
        // battle:end
        return {
          ...b,
          status: "ended",
          winner_id: msg.winner_id,
          host_score: msg.host_score,
          opponent_score: msg.opponent_score,
        };
      });
    },
  });
  // Authoritative room doc: initial prop → WS snapshot (hello/sync) → events.
  const freshStream = useFreshStream(stream, roomSocket);
  const pollStage = freshStream?.stage || DEFAULT_STAGE;
  const serverStage = wsStage || pollStage;
  const stage = stageOverride || serverStage;
  // Ref mirrors serverStage so `applyStagePatch`'s catch branch reverts to the
  // LATEST server truth (not the value captured when the handler was created).
  const serverStageRef = useRef(serverStage);
  useEffect(() => { serverStageRef.current = serverStage; });

  const applyStagePatch = async (patch) => {
    // Functional update so back-to-back host taps (e.g. spotlight → pin) don't
    // race and drop the earlier optimistic value.
    setStageOverride((prev) => ({ ...(prev || serverStageRef.current), ...patch }));
    try {
      const { data } = await api.post(`/live/${stream.id}/stage`, patch);
      if (data?.stage) setStageOverride(data.stage);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update the stage.");
      setStageOverride(serverStageRef.current);
    }
  };

  // Once the polling loop catches up to (or past) our optimistic override,
  // release it so pin/spotlight cleared by another admin session reflects here.
  useEffect(() => {
    if (!stageOverride || !serverStage?.updated_at) return;
    if (!stageOverride.updated_at || stageOverride.updated_at <= serverStage.updated_at) {
      setStageOverride(null);
    }
  }, [serverStage?.updated_at, stageOverride]);

  // Room state rides on the single WS: presence + host heartbeat on ping/pong,
  // AFK on pong/brb:changed/sync, stream end on stream:end/live:end/4404.
  useHostAfkFromSocket({ socket: roomSocket, onAfk: setAfk });
  // The moment a viewer's raise-hand request is accepted (either by the
  // host clicking accept, or the host invite-to-join flow completing),
  // this poll notices and re-mints the LiveKit token so the guest goes
  // on-stage automatically — no manual banner tap, no page reload.
  useCohostStatusWatch({
    isHost,
    isCohost,
    streamId: stream.id,
    userId: user?.id,
    socket: roomSocket,
    onAccepted: () => {
      toast.success("You're on stage!");
      refreshToken();
    },
  });
  const viewers = useViewersList({ socket: roomSocket });
  const nextLive = useNextLive({ streamId: stream.id, userLocation: user?.location, enabled: !!onSurf });
  const { battle, setBattle, reload: reloadBattle } = useBattlePoll(stream.id, roomSocket);
  // Expose setBattle to the room WS handler defined above via a ref so the
  // WS can push scoreboard updates without waiting for the next 4s poll.
  useEffect(() => { setBattleRef.current = setBattle; }, [setBattle]);

  // Slice 2+3 — late joiners recover any open game
  useEffect(() => {
    let gone = false;
    api.get(`/live/${stream.id}/games/active`)
      .then(({ data }) => { if (!gone && data.game) setActiveGame(data.game); })
      .catch(() => {});
    return () => { gone = true; };
  }, [stream.id]);
  useMuxLiveTracking({ muxEnvKey, token, stageRef, stream, userId: user?.id });

  // Stage 4(c) — WS `stream:end` signal is the fastest path to close the room
  // (< 100 ms) vs the freshStream poll's 6 s worst-case.
  useEffect(() => {
    if (wsEnded && onClose) onClose();
  }, [wsEnded, onClose]);

  // Live Restart Investigation — Preview-only instrumentation. Emits one
  // event per interesting lifecycle transition (mount, token refresh, WS
  // reconnect, auth swap, route change, visibility, sw update, memory
  // pressure). All events carry a stable session_correlation_id so the
  // whole timeline of one Live visit is grep-able server-side. No PII, no
  // message contents. Server is behind LIVE_MOUNT_TELEMETRY_ENABLED so the
  // hook is safe to ship dark.
  useLiveMountTelemetry({
    streamId: stream.id,
    role,
    token,
    wsConnected,
    authUserId: user?.id,
  });

  const toggleBrb = async () => {
    try {
      const next = !(afk?.brb_active);
      const { data } = await api.post(`/live/${stream.id}/brb`, { on: next });
      if (data?.afk) setAfk(data.afk);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Could not toggle BRB.");
    }
  };

  const surfNext = useCallback(() => {
    if (nextLive && onSurf) onSurf(nextLive);
  }, [nextLive, onSurf]);

  const onTouchStart = (e) => {
    if (!e.touches?.length) return;
    touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, t: Date.now() };
  };
  const onTouchEnd = (e) => {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!start || !e.changedTouches?.length) return;
    const dx = e.changedTouches[0].clientX - start.x;
    const dy = e.changedTouches[0].clientY - start.y;
    const dt = Date.now() - start.t;
    if (dx < -80 && Math.abs(dx) > Math.abs(dy) * 1.5 && dt < 800) surfNext();
  };

  // Iter61 — End Live reliability. Each tap does one attempt + one internal
  // retry (~800ms delay) so a single flaky request rarely surfaces to the
  // host. If BOTH network attempts fail we surface a red toast and let the
  // host tap End Live again. After 3 accumulated failed taps the host is
  // "trapped" (backend truly unreachable), so we force-close the LiveRoom
  // client-side. From the host's perspective the room is over; if the
  // backend eventually catches up the stream doc will flip is_live=false on
  // its own via the AFK auto-end path (no more host heartbeats coming in).
  const [ending, setEnding] = useState(false);
  const endAttemptsRef = useRef(0);
  const END_MAX_ATTEMPTS = 3;
  const endStream = async () => {
    if (ending) return;
    setEnding(true);
    const callEndOnce = () => api.post(`/live/${stream.id}/end`);
    try {
      try {
        await callEndOnce();
      } catch (firstErr) {
        // retry-once after a brief delay to absorb transient blips
        await new Promise((r) => setTimeout(r, 800));
        await callEndOnce();
      }
      toast.success("Stream ended.");
      endAttemptsRef.current = 0;
      onClose && onClose();
    } catch {
      endAttemptsRef.current += 1;
      if (endAttemptsRef.current >= END_MAX_ATTEMPTS) {
        // Force-end fallback so the host isn't trapped in the room. The
        // stream doc will end server-side once the AFK timer trips.
        toast.error(
          "End Live keeps failing — force-closing the room. If viewers still see you live, please refresh.",
          { duration: 6000 }
        );
        endAttemptsRef.current = 0;
        onClose && onClose();
      } else {
        toast.error(
          `Could not end stream (attempt ${endAttemptsRef.current}/${END_MAX_ATTEMPTS}). Tap End Live again to retry.`
        );
      }
    } finally {
      setEnding(false);
    }
  };

  const sendGift = async (giftKey, message) => {
    await api.post(`/live/${stream.id}/gift`, { gift_key: giftKey, message, to_user_id: giftTarget?.id || undefined });
    requestVipCheck();
  };
  const openGiftFor = (target) => { setGiftTarget(target || null); setGiftOpen(true); };
  const onGiftOpenChange = (o) => { setGiftOpen(o); if (!o) setGiftTarget(null); };

  const shareUrl = `${window.location.origin}/live?s=${stream.id}`;
  const copyShare = async () => {
    try {
      const displayTitle = stream.title || "Untitled Live";
      const shareText = `🔴 LIVE now on My Panda X — "${displayTitle}" — join us: ${shareUrl}`;
      if (navigator.share) {
        try { await navigator.share({ title: displayTitle, text: shareText, url: shareUrl }); return; } catch { /* fallthrough */ }
      }
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      toast.success("Share link copied!");
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Could not copy link."); }
  };

  // Tap the video → burst a floating heart, TikTok-style.
  const spawnHeart = (e) => {
    if (!e) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const id = ++heartCounter.current;
    setHearts((cur) => [...cur, { id, x, y }]);
    // server-side hearts counter: optimistic +1, batched POST every 900 ms
    setHeartsTotal((t) => t + 1);
    pendingHearts.current += 1;
    if (!heartFlush.current) {
      heartFlush.current = setTimeout(async () => {
        const n = pendingHearts.current; pendingHearts.current = 0; heartFlush.current = null;
        try { const { data } = await api.post(`/live/${stream.id}/hearts`, { count: Math.min(n, 30) }); if (data?.hearts_total != null) setHeartsTotal(data.hearts_total); } catch { /* ignore */ }
      }, 900);
    }
    setTimeout(() => setHearts((cur) => cur.filter((h) => h.id !== id)), 1800);
  };

  if (paywall) {
    return <PaidLivePaywall streamId={stream.id} hostName={stream.host?.display_name} price={paywall.price} onUnlocked={refreshToken} onClose={onClose} />;
  }

  if (err) {
    return (
      <div className="fixed inset-0 z-[80] bg-black flex items-center justify-center p-6">
        <div className="glass p-10 max-w-lg text-center border border-white/10">
          <div className="text-xs uppercase tracking-[0.3em] text-rose-500 mb-3">Live · Unavailable</div>
          <h2 className="font-display text-3xl mb-4">Stream can't start right now.</h2>
          <p className="text-sm text-zinc-400 leading-relaxed mb-6">{err}</p>
          <button
            type="button"
            onClick={onClose}
            className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold px-6 py-3 rounded-full"
          >
            Back to Live
          </button>
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="fixed inset-0 z-[80] bg-black flex items-center justify-center text-zinc-400">
        Connecting to the stream…
      </div>
    );
  }

  const wheelState = freshStream?.wheel_state || null;
  const hasWheel = wheelState
    ? !!wheelState.enabled || (isHost && (wheelState.segments || []).length >= 2)
    : (freshStream?.wheel || []).filter((s) => (s || "").trim()).length >= 2;
  const battleActive = !!(battle && (battle.status === "pending" || battle.status === "active"));

  // Iter67 — Fix A + left/right layout: keep LiveRoomStage continuously
  // mounted through battle start/end (Fix A) and, when a battle is active,
  // constrain it to its LEFT or RIGHT half of the viewport (portrait AND
  // landscape). Host A = left, Host B = right, matching the design ref.
  // BattleSplitLayout renders as a sibling overlay that draws the opponent
  // peek pane on the OPPOSITE half + a thin center divider. Chat/gift
  // overlays continue to sit at the bottom edge and remain accessible.
  const battleIsLive = !!(battle && battle.status === "active");
  const myStreamIsHostSide = battleIsLive && stream?.id === battle?.host_stream_id;
  const myStreamIsOppSide  = battleIsLive && stream?.id === battle?.opponent_stream_id;

  let stageWrapperClass = "absolute inset-0";
  // Duo (regular 2-person Live, host + 1 guest): video band on top, chat in
  // its own region below. Never applies to battles, Community rooms, solo,
  // or 3+ multi-guest grids.
  const duoActive = !battleIsLive && publisherCount === 2;
  // Multi-box (3+ guests): same band geometry, taller so 2×2 / 3×2 grids keep
  // faces large; chat lives in the freed lower region, never over a face.
  const multiActive = !battleIsLive && publisherCount >= 3;

  // Stage approval is a session grant: a deliberate leave (Leave box, or the
  // guest closing the Live) revokes it server-side; coming back means
  // Request-to-Join / a new invite again.
  const leaveBox = async () => {
    try { await api.post(`/live/${stream.id}/cohost/leave`); } catch { /* best effort */ }
    toast("You left the box — request to join again anytime.");
    refreshToken();
  };
  const handleGuestClose = () => {
    if (isCohost && !isHost) api.post(`/live/${stream.id}/cohost/leave`).catch(() => {});
    onClose?.();
  };
  const controlCenter = {
    streamId: stream.id,
    userId: user?.id,
    hostName: stream?.host?.display_name || stream?.host?.handle || community?.name || "Host",
    viewerCount: wsConnected ? wsViewerCount : viewers.length,
    goal: freshStream?.goal_coins ? { coins: freshStream.goal_coins, progress: freshStream.goal_progress || 0, label: freshStream.goal_label } : null,
    requestsCount: pendingRequests,
    canRequestJoin: caps.hasCohost && !isHost && !isCohost,
    onOpenRequests: () => window.dispatchEvent(new CustomEvent("pandax:open-cohost-requests")),
    onOpenSettings: () => setToolsOpen(true),
    onShare: copyShare,
    onReport: () => setReportOpen(true),
    onEndStream: isHost ? endStream : undefined,
    onOpenAdmin: isHost && isAdminUser ? () => setAdminOpen(true) : undefined,
    onLeaveBox: isCohost && !isHost ? leaveBox : undefined,
  };

  const muteGuest = async (identity) => {
    try {
      await api.post(`/live/${stream.id}/guests/mute`, { user_id: identity, muted: true });
      toast.success("Guest mic muted — they can unmute themselves");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't mute that guest's mic");
    }
  };
  if (myStreamIsHostSide) {
    // Side "A" (host_stream_id) always lives on the LEFT half of the arena.
    stageWrapperClass = "absolute top-0 h-[var(--battle-arena-h)] left-0 w-1/2 overflow-hidden";
  } else if (myStreamIsOppSide) {
    // Side "B" (opponent_stream_id) always lives on the RIGHT half of the arena.
    stageWrapperClass = "absolute top-0 h-[var(--battle-arena-h)] right-0 w-1/2 overflow-hidden";
  } else if ((duoActive || multiActive) && shortLandscape) {
    stageWrapperClass = "absolute left-0 bottom-0 w-[66%] overflow-hidden top-[calc(env(safe-area-inset-top,0px)+var(--live-header-h,3rem)+0.25rem)]";
  } else if (duoActive || multiActive) {
    // Duo/multi — full-width upper band that starts BELOW the compact header
    // (Part 6) so no chip ever sits on a top-row face; chat owns the rest.
    stageWrapperClass = "absolute inset-x-0 h-[var(--battle-arena-h)] overflow-hidden top-[calc(env(safe-area-inset-top,0px)+var(--live-header-h,3rem)+0.5rem)]";
  }
  const sideBySide = (duoActive || multiActive) && shortLandscape && !battleIsLive;

  return (
    <LiveRoomSocketContext.Provider value={roomSocket}>
    <UserBadgeProvider context="live">
    <div
      className="fixed inset-0 z-[80] bg-black overflow-hidden select-none"
      style={{
        touchAction: "manipulation",
        overscrollBehavior: "contain",
        // Versus arena height — responsive (small iPhones → desktop), never
        // a rigid vh. Read by the battle panes, divider, score row and chat.
        "--battle-arena-h": multiActive ? "clamp(320px, 62dvh, 760px)" : "clamp(240px, 42dvh, 520px)",
      }}
      data-testid="live-room"
      data-band={sideBySide ? "side" : duoActive || multiActive ? "offset" : "full"}
      data-stage-side={sideBySide ? "left" : undefined}
      ref={rootRef}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Iter67 Fix A — LiveRoomStage always mounted at a stable tree
          position. `stageWrapperClass` constrains it to its own half during
          an active battle. Do NOT re-nest this inside BattleSplitLayout. */}
      <div className={stageWrapperClass} data-testid="live-stage-wrapper">
        <LiveRoomStage
          token={token}
          serverUrl={serverUrl}
          canPublish={canPublish}
          stageRef={stageRef}
          onTap={spawnHeart}
          stage={stage}
          isHost={isHost}
          canModerate={canModerate}
          streamId={stream.id}
          role={role}
          onTileAction={applyStagePatch}
          canKickParticipant={caps.canKickParticipant}
          onKickParticipant={onKickParticipant}
          onMuteGuest={muteGuest}
          onLeaveBox={isCohost && !isHost ? leaveBox : undefined}
          hostId={stream?.host?.id || stream?.host_id}
          duo={duoActive}
          onPublisherCount={setPublisherCount}
          onGiftCohost={(t) => (t?.id && t.id !== user?.id ? openGiftFor(t) : null)}
          controlCenter={controlCenter}
          audioTestAllowed={!!user?.audio_test_allowed}
          publisherCount={publisherCount}
        />
      </div>

      {battleIsLive && (
        // Iter66/67 — overlay-only: renders the opponent peek pane on the
        // opposite half + VS/score/timer divider. Does NOT render another
        // LiveRoomStage. LiveRoomStage above stays mounted the whole time.
        // Iter68 — `audioBridgeReady` tells BattleOpponentPane to suppress
        // its own <RoomAudioRenderer /> because opponent audio is now
        // being delivered natively into the main LiveRoomStage room via
        // the server-side bridge. If null/"pending"/"failed"/"stopped",
        // the pane keeps its legacy audio path (unchanged behaviour).
        <BattleSplitLayout
          stream={stream}
          battle={battle}
          currentUserId={user?.id}
          audioBridgeReady={battle?.audio_bridge_state === "ready"}
        />
      )}

      {/* Live 2.0 — ultra-soft edge scrims. Video is the hero; only enough
          gradient to keep the top chips + bottom controls legible. No hard
          bands over the center where faces sit. */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-24 z-10 bg-gradient-to-b from-black/55 via-black/15 to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-32 z-10 bg-gradient-to-t from-black/60 via-black/15 to-transparent" />

      <LiveRoomAfkBanner afk={afk} />

      {caps.hasCohost && (
        <>
          <LiveRoomCohostBanner
            streamId={stream.id}
            onAccepted={() => {
              // Feb 2026 — no more page reload. Refresh the LiveKit token
              // in-place; the guest transitions viewer → cohost seamlessly
              // (same path the raise-hand acceptance uses).
              toast.success("You're on stage!");
              refreshToken();
            }}
          />
          <LiveRoomCohostRequests streamId={stream.id} isHost={isHost} actorRole={roomRole} />
        </>
      )}

      <LiveRoomHostStageControls
        streamId={stream.id}
        stage={stage}
        isHost={isHost}
        premium={!!(user?.is_panda_plus || user?.is_owner)}
        onStageChange={(s) => s && setStageOverride(s)}
      />

      <LiveRoomTopBar
        stream={stream}
        role={role}
        viewerCount={wsConnected ? wsViewerCount : viewers.length}
        giftCount={giftTotal}
        heartsTotal={heartsTotal}
        copied={copied}
        onToggleViewers={() => setShowViewers((s) => !s)}
        onShare={copyShare}
        onClose={handleGuestClose}
        onOpenBattle={caps.hasBattle && isHost && !battleActive ? () => setBattleOpen(true) : null}
        isHost={isHost}
        onEndStream={isHost ? endStream : null}
        topGifters={topGifters}
        headerRef={headerRef}
      >
        {!isHost && caps.hasGoalBar && <LiveGoalBar streamId={stream.id} initial={freshStream} variant="chip" />}
        {triviaTrigger > 0 && <TriviaTopChip streamId={stream.id} trigger={triviaTrigger} />}
      </LiveRoomTopBar>

      {caps.hasBattle && battle && (battle.status === "active" || battle.status === "ended") && (
        <div
          className={
            battle.status === "active"
              ? "absolute top-[var(--battle-arena-h)] inset-x-0 z-20" // reserved row under the arena — never overlaps video
              : "absolute top-16 inset-x-3 z-20"
          }
        >
          <BattleBanner battle={battle} viewerStreamId={stream.id} />
        </div>
      )}

      {/* Iter66 — the tile-sized BattlePeekTile is replaced by the full
          50/50 BattleSplitLayout above when battle.status === "active".
          The tile mount is retained for `pending`/"ended" transient states
          (mostly a no-op safety net); the split layout is the primary UX
          during the active window. */}
      {caps.hasBattle && isHost && battle && battle.status === "pending" && (
        <div className="absolute top-28 right-3 z-30 pointer-events-auto">
          <BattlePeekTile
            battleId={battle.id}
            enabled
            audioBridgeReady={battle?.audio_bridge_state === "ready"}
            opponentHandle={
              stream.id === battle.host_stream_id
                ? battle.opponent?.display_name
                : battle.host?.display_name
            }
          />
        </div>
      )}

      {/* Iter60 — earnings + top-gifter chips. Anchored below the top bar,
          on the LEFT so they don't crowd the top-right toolbar or the
          right-side action rail. Earnings is host-only. When a battle is
          active the BattleBanner sits at top-16 across the width, so we
          drop the chips below it.
          Slice B (Feb 2026) — hidden for HOST since the same data lives
          at the top of the Creator Tools wrench. Viewers still see the
          top-gifter chip on-stage. */}
      {/* Slice C — viewer info row: Top Gifter + Goal merged into ONE slim
          left-anchored row (goal chip auto-collapses after 6s) so only a
          single layer sits over the video. Host keeps the original centered
          goal bar — host chrome untouched. */}
      {caps.hasGoalBar && isHost && <LiveGoalBar streamId={stream.id} initial={freshStream} />}
      {(activeGame || dare.game) && (
        <div className="absolute left-0 right-[100px] md:right-[190px] z-30 pointer-events-none flex flex-col" data-testid="live-game-stack" style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 116px)" }}>
          {dare.game && (
            <DareGameCard
              game={dare.game}
              me={user}
              isHost={isHost}
              canModerate={canModerate}
              canJoin={dare.can_join}
              onRefresh={refreshDare}
              onGame={(g) => setDare((d) => ({ ...d, game: g }))}
              onDismiss={() => setDare({ game: null, can_join: false })}
            />
          )}
          {activeGame && (
            <LiveGameCard
              game={activeGame}
              isHost={isHost}
              onDismiss={() => setActiveGame(null)}
            />
          )}
        </div>
      )}

      <LiveChatOverlay streamId={stream.id} me={user} isHost={isHost} silent={canPublish} battleActive={caps.hasBattle && battle?.status === "active"} duoActive={duoActive || multiActive} sideBySide={sideBySide} />
      <LiveGiftOverlay streamId={stream.id} silent={canPublish} />
      <LiveRoomHearts hearts={hearts} />

      <LiveRoomActionRail
        nextLive={caps.hasNextLive ? nextLive : null}
        onSurf={caps.hasNextLive && onSurf ? surfNext : null}
        onOpenGift={isHost ? null : () => setGiftOpen(true)}
        onOpenTools={isHost ? () => setToolsOpen(true) : null}
        toolsTitle="Actions"
        hideGift={caps.hasBattle && battleActive && !isHost}
        extra={(
          <>
            {caps.hasCohost && !isHost && !isCohost ? <LiveRoomRequestCohostButton streamId={stream.id} userId={user?.id} /> : null}
          </>
        )}
      />

      {/* Feb 2026 — Battle Primary Gift CTA. During an active 1v1 Battle
          the right-rail Gift FAB is hidden (see `hideGift` above) and this
          bottom-center pill becomes the viewer's primary gifting action.
          Gift logic (GiftPicker + /live/{id}/gift) is untouched — only the
          affordance/position changes. Host is anti-self-gifting so the
          CTA is gated on `!isHost` here too. */}
      {caps.hasBattle && battleActive && !isHost && (
        <BattleGiftCta onOpenGift={() => setGiftOpen(true)} />
      )}

      <LiveRoomCreatorTools
        open={toolsOpen}
        onOpenChange={setToolsOpen}
        isHost={isHost}
        streamId={stream.id}
        viewerCount={wsConnected ? wsViewerCount : viewers.length}
        copied={copied}
        caps={{
          hasWheel: caps.hasWheel && hasWheel,
          hasBattle: caps.hasBattle,
          hasCohost: caps.hasCohost,
          hasBrb: caps.hasBrb,
          hasStreamPoll: caps.hasStreamPoll,
          hasGoalBar: caps.hasGoalBar,
          hasPaidLive: true,
          hasRevenueSplit: true,
          hasExtensions: true,
          hasBeauty: true,
          hasFilters: true,
          hasCustomGifts: true,
        }}
        battleActive={caps.hasBattle && battleActive}
        battleId={battle?.id || null}
        brbActive={!!afk?.brb_active}
        onOpenWheel={caps.hasWheel ? () => setWheelOpen(true) : null}
        onOpenGoal={() => setLiveToolsOpen(true)}
        onOpenBeauty={() => setLiveToolsOpen(true)}
        onOpenFilters={() => setLiveToolsOpen(true)}
        onOpenCustomGifts={() => setLiveToolsOpen(true)}
        onOpenSplit={() => setLiveToolsOpen(true)}
        onOpenExtensions={() => setLiveToolsOpen(true)}
        onOpenGames={(t) => { setGameType(t); setGamesOpen(true); }}
        onOpenDare={() => setDareOpen(true)}
        onOpenBlast={() => setBlastOpen(true)}
        onOpenBattle={caps.hasBattle ? () => setBattleOpen(true) : null}
        onOpenCohost={caps.hasCohost ? () => setCohostOpen(true) : null}
        onToggleBrb={caps.hasBrb ? toggleBrb : null}
        onOpenViewers={() => setShowViewers(true)}
        onShare={copyShare}
        onEndStream={isHost ? endStream : null}
        onOpenAdmin={isAdminUser ? () => setAdminOpen(true) : null}
        onForfeitOptimistic={reloadBattle}
      />
      {isAdminUser && <LiveRoomAdminDrawer open={adminOpen} onOpenChange={setAdminOpen} />}

      {showViewers && (
        <LiveRoomViewersDrawer
          viewers={viewers}
          onClose={() => setShowViewers(false)}
          streamId={stream.id}
          isHost={isHost}
          actorRole={roomRole}
        />
      )}

      {isHost && (
        <LiveToolsPanel
          open={liveToolsOpen}
          onOpenChange={setLiveToolsOpen}
          stream={freshStream || stream}
        />
      )}

      {isHost && (
        <GamesLaunchSheet
          key={gameType}
          open={gamesOpen}
          onOpenChange={setGamesOpen}
          streamId={stream.id}
          initialType={gameType}
        />
      )}

      {isHost && (
        <DareOrShotSheet open={dareOpen} onOpenChange={setDareOpen} streamId={stream.id} />
      )}

      {isHost && (
        <LiveBlastSheet open={blastOpen} onOpenChange={setBlastOpen} streamId={stream.id} />
      )}

      <GiftPicker
        open={giftOpen}
        onOpenChange={onGiftOpenChange}
        onSend={sendGift}
        context="live"
        recipientName={giftTarget ? (giftTarget.name || "co-host") : (stream.host?.display_name || community?.name)}
        wheel={caps.hasWheel && !isHost ? wheelState : null}
        onOpenWheel={caps.hasWheel ? () => setWheelOpen(true) : null}
      />
      {caps.hasCohost && (
        <CoHostInviteModal
          open={cohostOpen}
          onOpenChange={setCohostOpen}
          streamId={stream.id}
        />
      )}
      {caps.hasBattle && (
        <BattleModal
          open={battleOpen}
          onOpenChange={setBattleOpen}
          myStreamId={stream.id}
          onCreated={reloadBattle}
        />
      )}
      <ReportModal
        open={reportOpen}
        onOpenChange={setReportOpen}
        target={{ type: "live", id: stream.id, name: stream?.host?.display_name || stream?.title || "this Live" }}
      />
      {caps.hasWheel && (
        <LiveWheelModal
          open={wheelOpen}
          onOpenChange={setWheelOpen}
          streamId={stream.id}
          segments={freshStream?.wheel || []}
          wheel={wheelState}
        />
      )}
    </div>
    </UserBadgeProvider>
    </LiveRoomSocketContext.Provider>
  );
}
